// Copying one trip leg into the Captain's Log, from either of the two surfaces
// that offer it: the leg's own page (Trip.jsx) and a row of its trip's page
// (TripGroup.jsx).
//
// The sequencing lives here rather than on the two pages because it is the part
// that is easy to get subtly wrong and impossible to notice: flush the leg's
// autosave, wait out its lane AND the date cascade, and only then read. Two
// copies of that would be two chances to drop a step.
//
// Returns [modals, controls] in useStatusMessage's shape, so a page renders one
// value and calls one function. `modals` is an ELEMENT rather than a component
// -- see the note where it is built.
//
// THE LEG HANDED IN IS NEVER THE LEG COPIED. TripGroup's rows are display
// copies carrying gazetteer-resolved From/To names, and Trip.jsx's in-memory
// record is updated asynchronously by its autosave's onSaved. Only the id is
// taken from the argument; the row itself is re-read from the server. That is
// onDetails' existing "re-look-up the stored leg" rule applied to a write path,
// where it actually binds -- a resolved name reaching this create would write a
// display artifact into the logbook.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';

import * as Database from '../../utilities/Database.mjs';
import { saveQueue } from '../../utilities/SaveQueue.mjs';
import { legLane, settledLegCascades } from '../../utilities/TripSchedule.mjs';
import { legToEntryInput } from '../../fleet-shared/TripToLogbook.mjs';
import {
  LOGBOOK_TYPE_CAPTAINS,
  defaultNameForType,
  entryTitle,
} from '../../fleet-shared/LogbookTypes.mjs';
import { urlForLogbookEntry } from '../../fleet-shared/Urls.mjs';
import ChooseLogbookModal from '../logbook/ChooseLogbookModal';
import CopyLegToLogModal from './CopyLegToLogModal';

dayjs.extend(utc);

// The leg's day, spelled the way the trip list spells it. Parsed in UTC to
// match utcDayIndex -- the reading every stored trip date takes -- so the
// warning names the same day the row the user clicked from does.
function dayLabelFor(date) {
  const d = dayjs.utc(date);
  return d.isValid() ? d.format('ll') : '';
}

export function useCopyLegToLogbook({ item, itemId, showText, showError, hideStatus }) {
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  // The leg + whatever has been resolved so far, while a modal is open.
  const [pending, setPending] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [warning, setWarning] = useState(null);

  // One copy at a time, held in a ref rather than read off `busy`.
  //
  // `busy` is state, so a second click in the same tick sees the old value --
  // and disabling the button is not enough on its own either, since the flag
  // has to reach the caller and TripGroup's rows are a second surface that
  // could forget to wire it. There is a real gap to slip through: retrieveTrip
  // -> listLogbooks -> the day read is several round trips, and two runs would
  // both pass the `matches.length === 0` check before either create landed,
  // producing two entries for one day. The guard lives here so neither caller
  // can be the one that gets it wrong.
  const running = useRef(false);

  // A lane that never drains would otherwise hold the settled() subscription
  // for the life of the page; aborting resolves the wait, and the mounted
  // re-check on the far side of each await handles the rest.
  const leaving = useRef(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    leaving.current = controller;
    return () => {
      mounted.current = false;
      controller.abort();
    };
  }, []);

  // Ends the run, whether it finished, failed or was cancelled. The guard is
  // released here and nowhere else -- a flow paused on one of the modals is
  // still a run in progress, or a click on another row would clobber `pending`
  // out from under the open dialog.
  const reset = useCallback(() => {
    running.current = false;
    setPending(null);
    setChoosing(false);
    setWarning(null);
    setBusy(false);
  }, []);

  // Last step: build the input, create, and open the new entry.
  const createEntry = useCallback(async (stored, logbook) => {
    try {
      setBusy(true);
      showText('Copying...');
      const input = legToEntryInput(stored, { itemId, logbookId: logbook.id });
      const created = await Database.createCompletedTask(input);
      if (!mounted.current) return;
      hideStatus();
      reset();
      // Plain state handover of the item only: the entry page must read its own
      // brand-new row rather than trust a snapshot built here.
      navigate(urlForLogbookEntry(itemId, logbook.id, created.id), { state: { item } });
    } catch (err) {
      // Without this a failed create leaves the page on "Copying..." with the
      // error surfacing only as an unhandled rejection.
      if (mounted.current) {
        reset();
        showError(err);
      }
    }
  }, [itemId, item, navigate, showText, showError, hideStatus, reset]);

  // The duplicate question is per-logbook, so it is asked after the logbook is
  // known -- not before.
  const withLogbook = useCallback(async (stored, logbook) => {
    try {
      setChoosing(false);
      setBusy(true);
      showText('Checking...');
      const matches = await Database.listLogbookEntriesOnDay(logbook.id, stored.date);
      if (!mounted.current) return;
      if (matches.length === 0) {
        await createEntry(stored, logbook);
        return;
      }
      hideStatus();
      setBusy(false);
      setPending({ stored, logbook });
      setWarning({
        dayLabel: dayLabelFor(stored.date),
        matches: matches.map((entry) => ({
          id: entry.id,
          title: entryTitle(entry, LOGBOOK_TYPE_CAPTAINS) || 'Entry',
        })),
      });
    } catch (err) {
      if (mounted.current) {
        reset();
        showError(err);
      }
    }
  }, [createEntry, showText, showError, hideStatus, reset]);

  // Entry point. `leg` need only carry a valid id.
  const copyLegToLogbook = useCallback(async (leg) => {
    if (!leg?.id || running.current) return;
    try {
      running.current = true;
      setBusy(true);
      showText('Loading logbooks...');

      // Flush is a synchronous kick that returns nothing, so it has to be
      // followed by a wait. Then the lane, then the cascade -- in that order,
      // because a leg's date cascade is queued from the save's onSaved, which
      // the queue runs before it notifies the subscribers settled() resolves on.
      const signal = leaving.current?.signal;
      saveQueue.flush(legLane(leg.id));
      await saveQueue.settled(legLane(leg.id), { signal });
      await settledLegCascades();
      if (!mounted.current) return;

      const stored = await Database.retrieveTrip(leg.id);
      if (!mounted.current) return;
      if (!stored) throw new Error('That leg no longer exists.');

      const all = await Database.listLogbooks(itemId);
      if (!mounted.current) return;
      const captains = all.filter((lb) => lb.logbookType === LOGBOOK_TYPE_CAPTAINS);

      if (captains.length === 1) {
        await withLogbook(stored, captains[0]);
        return;
      }

      // None or several: ask. The modal covers both -- with none it offers to
      // create one instead of listing.
      hideStatus();
      setBusy(false);
      setPending({ stored, logbooks: captains });
      setChoosing(true);
    } catch (err) {
      if (mounted.current) {
        reset();
        showError(err);
      }
    }
  }, [itemId, withLogbook, showText, showError, hideStatus, reset]);

  const createLogbookAndProceed = useCallback(async () => {
    const stored = pending?.stored;
    if (!stored) return;
    try {
      setChoosing(false);
      setBusy(true);
      showText('Creating logbook...');
      const created = await Database.createLogbook({
        itemId,
        name: defaultNameForType(LOGBOOK_TYPE_CAPTAINS),
        logbookType: LOGBOOK_TYPE_CAPTAINS,
      });
      if (!mounted.current) return;
      // Brand-new logbook, so it cannot already hold this day -- but go through
      // withLogbook anyway rather than shortcutting to createEntry, so there is
      // one path to a created entry.
      await withLogbook(stored, created);
    } catch (err) {
      if (mounted.current) {
        reset();
        showError(err);
      }
    }
  }, [pending, itemId, withLogbook, showText, showError, reset]);

  // An ELEMENT, not a component -- deliberately unlike useStatusMessage, whose
  // UI is a stateless div that costs nothing to remount. A component identity
  // rebuilt on every dep change remounts what it renders, and `busy` changes
  // while the warning modal is open: Bootstrap's Modal would tear down and
  // re-enter mid-create. Returned as an element, it keeps its position in the
  // tree and is reconciled instead.
  const modals = (
    <>
      <ChooseLogbookModal
        show={choosing}
        onHide={reset}
        logbooks={pending?.logbooks ?? []}
        onChoose={(logbook) => withLogbook(pending.stored, logbook)}
        onCreateNew={createLogbookAndProceed}
        title="Copy to Captain's Log"
        chooseText="Choose a Captain's Log for this day:"
        emptyText="This item has no Captain's Log yet. Create one to record this day."
        createLabel="New Captain's Log"
      />
      <CopyLegToLogModal
        show={Boolean(warning)}
        onHide={reset}
        onConfirm={() => createEntry(pending.stored, pending.logbook)}
        dayLabel={warning?.dayLabel}
        matches={warning?.matches ?? []}
        busy={busy}
      />
    </>
  );

  return [modals, { copyLegToLogbook, copying: busy }];
}

export default useCopyLegToLogbook;
